// pages/SignUpPage.tsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import type { EducationLevel } from '../types/user';

const EDUCATION_OPTIONS: EducationLevel[] = ['School', '12th', 'Diploma', 'Graduation', 'Post Graduation', 'Doctorate', 'Others'];

export default function SignUpPage() {
  const [step, setStep] = useState<'profile' | 'otp'>('profile');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [education, setEducation] = useState<EducationLevel>('School');
  const [otp, setOtp] = useState('');
  
  const sendOtp = useAuthStore((state) => state.sendOtp);
  const verifyOtp = useAuthStore((state) => state.verifyOtp);
  const createUserProfile = useAuthStore((state) => state.createUserProfile);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    // Firebase's RecaptchaVerifier handles reCAPTCHA internally.
    authService.setupRecaptcha('recaptcha-container-signup');
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendOtp(phone);
    const currentError = useAuthStore.getState().error;
    if (!currentError) {
      setStep('otp');
    } else {
      // Reset reCAPTCHA verifier after failure so user can retry
      authService.resetRecaptcha('recaptcha-container-signup');
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    await verifyOtp(otp);
    if (!useAuthStore.getState().error) {
      await createUserProfile({ displayName: name, email, currentEducation: education });
      if (!useAuthStore.getState().error) {
        navigate('/');
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md relative">
        {/* Skip Button - Top Right */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-sm font-medium"
        >
          Skip →
        </button>

        <h1 className="text-2xl font-bold text-center mb-2 text-primary">CareerNova Learning</h1>
        <p className="text-gray-600 text-center mb-6">Create your account</p>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div id="recaptcha-container-signup"></div>

        {step === 'profile' ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
              <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email (optional)</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number</label>
              <div className="flex mt-1">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">+91</span>
                <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Enter 10-digit number" required className="flex-1 block px-3 py-2 border border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
              </div>
            </div>
            <div>
              <label htmlFor="education" className="block text-sm font-medium text-gray-700">Education Level</label>
              <select id="education" value={education} onChange={(e) => setEducation(e.target.value as EducationLevel)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary">
                {EDUCATION_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
              </select>
            </div>
            <button type="submit" disabled={isLoading || phone.length !== 10 || !name} className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50">
              {isLoading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700">Enter OTP sent to +91 {phone}</label>
              <input id="otp" type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit OTP" required maxLength={6} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
            </div>
            <button type="submit" disabled={isLoading || otp.length !== 6} className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50">
              {isLoading ? 'Creating Account...' : 'Verify & Create Account'}
            </button>
            <button type="button" onClick={() => {
              setOtp('');
              useAuthStore.getState().clearError();
              setStep('profile');
              authService.resetRecaptcha('recaptcha-container-signup');
            }} className="w-full text-primary hover:underline">Go Back</button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/signin" className="text-primary hover:underline">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
